import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Star, Heart, Send, ExternalLink, TrendingUp, Users, Loader2 } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, tone = "emerald", testid }) {
  const T = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber:   "border-amber-200   bg-amber-50   text-amber-700",
    violet:  "border-violet-200  bg-violet-50  text-violet-700",
    rose:    "border-rose-200    bg-rose-50    text-rose-700",
    indigo:  "border-indigo-200  bg-indigo-50  text-indigo-700",
  }[tone];
  return (
    <div className={`rounded-xl border p-3 ${T}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-extrabold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 text-lg font-extrabold tabular-nums text-gray-900 font-[Outfit]">{value}</div>
      {sub && <div className="text-[10px] text-gray-500 truncate">{sub}</div>}
    </div>
  );
}

export default function ReviewsTipsDashboardCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/admin/reviews-tips-dashboard");
        setData(d);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center justify-center min-h-[100px]">
      <Loader2 className="w-5 h-5 animate-spin text-[#047857]" />
    </div>
  );
  if (!data) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="reviews-tips-dashboard-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-white flex items-center justify-center">
          <Star className="w-4 h-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-amber-700">Reviews &amp; Tips</div>
          <div className="text-lg font-extrabold tracking-tight text-gray-900 font-[Outfit]">Automation Dashboard</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Stat icon={Send}       label="Requests Sent"      value={data.review_requests_sent}         sub={`+${data.review_reminders_sent} reminders`} tone="indigo" testid="rt-stat-requests" />
        <Stat icon={ExternalLink} label="Link Clicks"      value={data.google_review_link_clicks}    sub={`on ${data.google_review_link_click_bookings} bookings`} tone="violet" testid="rt-stat-clicks" />
        <Stat icon={Star}       label="Reviews Completed"  value={data.reviews_completed}            sub="all-time"                                   tone="amber"  testid="rt-stat-reviews" />
        <Stat icon={Heart}      label="Tips Received"      value={data.tips_received}                sub={`avg $${data.average_tip_amount.toFixed(2)}`} tone="emerald" testid="rt-stat-tips" />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <Stat icon={TrendingUp} label="Total Tip Revenue"  value={`$${data.total_tip_revenue.toFixed(2)}`} sub="lifetime" tone="emerald" testid="rt-stat-revenue" />
        <Stat icon={Users}      label="Top Tippers"        value={data.top_tippers.length}                 sub="ranked below" tone="rose"    testid="rt-stat-toptippers" />
      </div>

      {data.top_tippers.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-1.5">Top Tipping Customers</div>
          <div className="space-y-1" data-testid="rt-top-tippers-list">
            {data.top_tippers.slice(0, 5).map((t, i) => (
              <div key={t.user_id || i} className="flex items-center justify-between bg-gray-50 rounded-lg px-2.5 py-1.5" data-testid={`rt-top-tipper-${i}`}>
                <div className="min-w-0">
                  <div className="text-[12px] font-extrabold text-gray-900 truncate">{t.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{t.email || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[12px] font-extrabold text-emerald-700 tabular-nums">${t.total_tipped.toFixed(2)}</div>
                  <div className="text-[10px] text-gray-500">{t.tip_count} tip{t.tip_count === 1 ? "" : "s"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
